import * as R from 'ramda';
import * as Yup from 'yup';
import React, { Fragment } from 'react';
import { endOfMonth, startOfMonth, differenceInMonths } from 'date-fns';
// components
import { ActionBox } from '../../../components/action-box';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import { vendorPayrollAccessorialPayForUnitTypeOptions } from '../../../helpers/options';
// icons
import * as I from '../../../svgs';
// ui
import { Box, RelativeBox } from '../../../ui';
// utilities
import routesMap from '../../../utilities/routes';
// feature fleet-profile
import { TableCell } from '../components/table-cells';
import VendorMonthlyPayrollDeductionForm from '../vendor/forms/payroll-compensation';
import { FormGroupTableWithSimpleCrudOperations } from '../components/form-group-table';
//////////////////////////////////////////////////

const calendarInputWrapperStyles = {
  width: '100%',
};

const customChangeHandler = (event, _, props) => {
  const { values, setValues } = props;

  const { id, value } = event.target;

  let valuesToSet = R.assoc(id, value, values);

  const amount = R.prop(GC.FIELD_AMOUNT, valuesToSet);
  const totalAmount = R.prop(GC.FIELD_TOTAL_AMOUNT, valuesToSet);
  const dateFrom = R.prop(GC.FIELD_DATE_FROM, valuesToSet);
  const dateTo = R.prop(GC.FIELD_DATE_TO, valuesToSet);

  const cond1 = G.isFalse(R.prop(GC.FIELD_LIMITED, values));

  const cond2 = G.isAllTrue(
    R.includes(id, [GC.FIELD_AMOUNT, GC.FIELD_TOTAL_AMOUNT, GC.FIELD_DATE_FROM]),
    G.notEquals(id, GC.FIELD_DATE_TO),
    G.isAllNotNilOrNotEmpty([amount, totalAmount, dateFrom]),
  );

  const cond3 = G.isAllTrue(
    R.includes(id, [GC.FIELD_TOTAL_AMOUNT, GC.FIELD_DATE_FROM, GC.FIELD_DATE_TO]),
    G.notEquals(id, GC.FIELD_AMOUNT),
    G.isAllNotNilOrNotEmpty([totalAmount, dateFrom, dateTo]),
  );

  if (cond1) {
    valuesToSet = R.mergeRight(valuesToSet, {
      [GC.FIELD_DATE_TO]: null,
      [GC.FIELD_TOTAL_AMOUNT]: null,
      [GC.FIELD_HISTORICAL_PAID_AMOUNT]: null,
    });
  } else if (cond2) {
    const count = Math.ceil(R.divide(totalAmount, amount));
    const dateTo = G.addDateTimeWithFormat(dateFrom, count, 'months', GC.DEFAULT_DATE_FORMAT);

    valuesToSet = R.assoc(GC.FIELD_DATE_TO, dateTo, valuesToSet);
  } else if (cond3) {
    const count = R.inc(Math.ceil(Math.abs(
      differenceInMonths(endOfMonth(new Date(dateTo)), startOfMonth(new Date(dateFrom))),
    )));

    const amount = R.divide(totalAmount, count);

    valuesToSet = R.assoc(GC.FIELD_AMOUNT, G.mathRoundNumber(amount), valuesToSet);
  }

  setValues(valuesToSet);
};

const customChangeHandlerForDatePickers = ({ value, props }) =>
  customChangeHandler(
    {
      target: {
        [GC.FIELD_ID]: G.getIdFromObject(props),
        [GC.FIELD_VALUE]: G.convertInstanceToDefaultDateFormat(value),
      },
    },
    '',
    props,
  );

const settings = {
  [GC.FIELD_PAYROLL_ASSESSORIAL_GUID]: {
    width: 200,
    isRequired: true,
    type: 'reactSelect',
    name: 'titles:accessorial2',
    options: 'accessorialConfigOptions',
    additionalInputWrapperStyles: { zIndex: 12 },
    customComponent: ({ data = {}, callbackData = {} }) => {
      const { payrollAssessorialGuid } = data;

      return R.path(
        ['accessorialConfigs', 'accessorialConfigList', payrollAssessorialGuid, GC.FIELD_DISPLAYED_VALUE],
        callbackData,
      );
    },
  },
  [GC.FIELD_DISPLAYED_VALUE]: {
    width: 200,
    name: 'titles:accessorial2',
    customComponent: ({ data = {}, callbackData = {} }) => {
      const { payrollAssessorialGuid } = data;

      const text = R.path(
        ['accessorialConfigs', 'accessorialConfigList', payrollAssessorialGuid, GC.FIELD_DISPLAYED_VALUE],
        callbackData,
      );

      return <TableCell text={text} />;
    },
    filter: {
      type: 'string',
      getFieldUiValue: (data, callbackData) => {
        const { payrollAssessorialGuid } = data;

        return R.path(
          ['accessorialConfigs', 'accessorialConfigList', payrollAssessorialGuid, GC.FIELD_DISPLAYED_VALUE],
          callbackData,
        );
      },
    },
  },
  [GC.FIELD_AMOUNT]: {
    width: 160,
    type: 'number',
    isRequired: true,
    customChangeHandler,
    name: 'titles:amount',
    shouldCustomChange: true,
    filter: { type: 'number' },
  },
  [GC.FIELD_TOTAL_AMOUNT]: {
    width: 160,
    type: 'number',
    isRequired: true,
    customChangeHandler,
    shouldCustomChange: true,
    filter: { type: 'number' },
    name: 'titles:total-amount',
    additionalInputWrapperStyles: {
      display: (props) => G.ifElse(
        R.pathEq(true, ['values', GC.FIELD_LIMITED], props),
        'flex',
        'none',
      ),
    },
  },
  [GC.FIELD_PAID_AMOUNT]: {
    width: 160,
    disabled: true,
    filter: { type: 'number' },
    name: 'titles:paid-amount',
    additionalInputWrapperStyles: {
      width: 220,
      display: ({ values }) => G.ifElse(
        R.isNil(G.getGuidFromObject(values)),
        'none',
      ),
    },
  },
  [GC.FIELD_HISTORICAL_PAID_AMOUNT]: {
    width: 160,
    type: 'number',
    customChangeHandler,
    shouldCustomChange: true,
    filter: { type: 'number' },
    name: 'titles:historical-paid-amount',
    additionalInputWrapperStyles: {
      display: (props) => G.ifElse(
        R.pathEq(true, ['values', GC.FIELD_LIMITED], props),
        'flex',
        'none',
      ),
    },
  },
  [GC.FIELD_CURRENCY]: {
    width: 160,
    type: 'select',
    name: 'titles:currency',
    options: GC.CURRENCY_OPTIONS,
    filter: { type: 'selectMultiple', options: GC.CURRENCY_OPTIONS },
  },
  [GC.FIELD_DATE_FROM]: {
    isRequired: true,
    type: 'datePicker',
    filter: { type: 'date' },
    name: 'titles:date-from',
    shouldCustomChange: true,
    calendarInputWrapperStyles,
    customChangeHandler2: customChangeHandlerForDatePickers,
  },
  [GC.FIELD_DATE_TO]: {
    isRequired: true,
    type: 'datePicker',
    name: 'titles:date-to',
    filter: { type: 'date' },
    shouldCustomChange: true,
    calendarInputWrapperStyles,
    customChangeHandler2: customChangeHandlerForDatePickers,
    additionalInputWrapperStyles: {
      visibility: (props) => G.ifElse(
        R.pathEq(true, ['values', GC.FIELD_LIMITED], props),
        'visible',
        'hidden',
      ),
    },
  },
  [GC.FIELD_PAY_FOR_UNIT]: {
    type: 'enum',
    fieldType: 'select',
    shouldCustomChange: true,
    name: 'titles:pay-for-unit',
    filterType: 'selectMultiple',
    options: vendorPayrollAccessorialPayForUnitTypeOptions,
    defaultValue: GC.VENDOR_PAYROLL_ACCESSORIAL_PAY_FOR_UNIT_TYPE_NONE,
    filter: { type: 'selectMultiple', options: vendorPayrollAccessorialPayForUnitTypeOptions },
    customChangeHandler: (event, _, props) => {
      const { values, setValues } = props;

      const newValues = R.mergeRight(
        values,
        {
          [GC.FIELD_TRUCK_GUIDS]: null,
          [GC.FIELD_DRIVER_GUIDS]: null,
          [GC.FIELD_PAY_FOR_UNIT]: R.path(['currentTarget', GC.FIELD_VALUE], event),
        },
      );

      setValues(newValues);
    },
  },
  [GC.FIELD_LIMITED]: {
    width: 160,
    type: 'boolean',
    defaultValue: true,
    fieldType: 'toggle',
    name: 'titles:limited',
    shouldCustomChange: true,
    filter: { type: 'boolean' },
    customChangeHandler: (event, _, { handleChange, setFieldValue }) => {
      handleChange(event);

      if (G.isFalse(G.getEventTargetChecked(event))) setFieldValue(GC.FIELD_DATE_TO, null);
    },
  },
  [GC.FIELD_DEDUCTION]: {
    width: 160,
    type: 'boolean',
    fieldType: 'toggle',
    defaultValue: false,
    name: 'titles:deduction',
    filter: { type: 'boolean' },
    additionalInputWrapperStyles: { width: 'max-content' },
    disabled: (_, { deductionDisabled = false }) => deductionDisabled,
  },
  [GC.FIELD_ACTIVE]: {
    width: 160,
    type: 'boolean',
    fieldType: 'toggle',
    defaultValue: false,
    name: 'titles:active',
    filter: { type: 'boolean' },
    additionalInputWrapperStyles: { width: 'max-content' },
  },
  [GC.FIELD_PROCESS_REMAINING_BALANCE]: {
    width: 170,
    type: 'boolean',
    fieldType: 'toggle',
    defaultValue: false,
    filter: { type: 'boolean' },
    name: 'titles:pay-deduct-remaining-balance',
    additionalInputWrapperStyles: { width: 'max-content' },
  },
  [GC.FIELD_FULLY_PAID]: {
    width: 160,
    type: 'boolean',
    hasLabel: false,
    name: 'titles:fully-paid',
    filter: { type: 'boolean' },
    fieldType: 'customComponent',
    additionalInputWrapperStyles: {
      width: 'max-content',
      display: (props) => G.ifElse(
        G.isNotNil(R.path(['values', GC.FIELD_FULLY_PAID], props)),
        'flex',
        'none',
      ),
    },
    Component: ({ value }) => {
      if (G.isNilOrEmpty(value)) return null;

      return (
        <Fragment>
          <Box mr={20}>{G.getWindowLocale('titles:fully-paid', 'Fully Paid')}</Box>
          {
            I[G.ifElse(G.isTrue(value), 'uiTrue', 'uiFalse')]()
          }
        </Fragment>
      );
    },
  },
  [GC.FIELD_COMMENTS]: {
    width: 300,
    type: 'textarea',
    name: 'titles:comments',
    additionalInputWrapperStyles: { width: 600 },
  },
  [GC.FIELD_TRUCK_GUIDS]: {
    isMulti: true,
    isRequired: true,
    type: 'reactSelect',
    name: 'titles:trucks',
    options: 'truckOptions',
    useMenuPortalTarget: true,
    additionalInputWrapperStyles: {
      zIndex: 11,
      visibility: (props) => G.ifElse(
        R.pathEq(
          GC.VENDOR_PAYROLL_ACCESSORIAL_PAY_FOR_UNIT_TYPE_SELECTED_TRUCKS,
          ['values', GC.FIELD_PAY_FOR_UNIT],
          props,
        ),
        'visible',
        'hidden',
      ),
      display: (props) => G.ifElse(
        R.pathEq(
          GC.VENDOR_PAYROLL_ACCESSORIAL_PAY_FOR_UNIT_TYPE_SELECTED_DRIVERS,
          ['values', GC.FIELD_PAY_FOR_UNIT],
          props,
        ),
        'none',
      ),
    },
  },
  [GC.FIELD_DRIVER_GUIDS]: {
    isMulti: true,
    isRequired: true,
    type: 'reactSelect',
    name: 'titles:drivers',
    options: 'driverOptions',
    useMenuPortalTarget: true,
    fieldName: GC.FIELD_DRIVER_GUIDS,
    additionalInputWrapperStyles: {
      zIndex: 11,
      visibility: (props) => G.ifElse(
        R.pathEq(
          GC.VENDOR_PAYROLL_ACCESSORIAL_PAY_FOR_UNIT_TYPE_SELECTED_DRIVERS,
          ['values', GC.FIELD_PAY_FOR_UNIT],
          props,
        ),
        'visible',
        'hidden',
      ),
      display: (props) => G.ifElse(
        R.pathEq(
          GC.VENDOR_PAYROLL_ACCESSORIAL_PAY_FOR_UNIT_TYPE_SELECTED_DRIVERS,
          ['values', GC.FIELD_PAY_FOR_UNIT],
          props,
        ),
        'flex',
        'none',
      ),
    },
  },
};

const totalsValidation = Yup.number()
  .nullable(true)
  .required(G.getRequiredLocaleTxt())
  .typeError(G.getShouldBeIntegerLocaleTxt())
  .min(0, G.getShouldBeFromToCharLocaleTxt(0, 999999))
  .max(9999999, G.getShouldBeFromToCharLocaleTxt(0, 999999));

const validationSchema = Yup.lazy(({ limited, payForUnit }) => Yup.object().shape({
  [GC.FIELD_AMOUNT]: totalsValidation,
  [GC.FIELD_CURRENCY]: G.yupStringRequired,
  [GC.FIELD_DATE_FROM]: G.yupStringRequired,
  [GC.FIELD_COMMENTS]: G.yupStringNotRequired,
  [GC.FIELD_FULLY_PAID]: Yup.boolean().nullable(true),
  [GC.FIELD_PAYROLL_ASSESSORIAL_GUID]: G.yupStringRequired,
  [GC.FIELD_HISTORICAL_PAID_AMOUNT]: Yup.number().nullable(true),
  [GC.FIELD_DATE_TO]: G.ifElse(G.isTrue(limited), G.yupStringRequired, G.yupStringNotRequired),
  [GC.FIELD_TOTAL_AMOUNT]: G.ifElse(G.isTrue(limited), totalsValidation, G.yupStringNotRequired),
  [GC.FIELD_DRIVER_GUIDS]: G.ifElse(
    R.equals(payForUnit, GC.VENDOR_PAYROLL_ACCESSORIAL_PAY_FOR_UNIT_TYPE_SELECTED_DRIVERS),
    G.yupArrayRequired,
  ),
  [GC.FIELD_TRUCK_GUIDS]: G.ifElse(
    R.equals(payForUnit, GC.VENDOR_PAYROLL_ACCESSORIAL_PAY_FOR_UNIT_TYPE_SELECTED_TRUCKS),
    G.yupArrayRequired,
  ),
}));

const defaultInputWrapperStyles = {
  mb: 25,
  width: 280,
};

const getDefaultReportFields = (fieldsToOmit) => R.compose(
  R.keys,
  R.omit(fieldsToOmit),
)(settings);

const defaultReportFields = getDefaultReportFields([
  GC.FIELD_TRUCK_GUIDS,
  GC.FIELD_DRIVER_GUIDS,
  GC.FIELD_PAY_FOR_UNIT,
  GC.FIELD_PAYROLL_ASSESSORIAL_GUID,
]);

const vendorDefaultReportFields = getDefaultReportFields([
  GC.FIELD_TRUCK_GUIDS,
  GC.FIELD_DRIVER_GUIDS,
  GC.FIELD_PAYROLL_ASSESSORIAL_GUID,
]);

const getReportFields = (fields) => G.mapIndexed(
  (name, sequence) => ({ name, sequence }),
  fields,
);

const report = {
  fields: getReportFields(defaultReportFields),
  [GC.FIELD_TYPE]: GC.DRIVER_PROFILE_UI_REPORTS,
  [GC.FIELD_GUID]: `${GC.DRIVER_PROFILE_UI_REPORTS}MonthlyPayrollDeduction`,
};

const vendorReport = {
  [GC.FIELD_TYPE]: GC.VENDOR_PROFILE_UI_REPORTS,
  fields: getReportFields(vendorDefaultReportFields),
  [GC.FIELD_GUID]: `${GC.VENDOR_PROFILE_UI_REPORTS}MonthlyPayrollDeduction`,
};

const defaultValues = R.map(({ defaultValue = null }) => defaultValue, settings);

const makeFieldSettings = (fields) => R.compose(
  R.values,
  R.mapObjIndexed(({
    name,
    label,
    options,
    isMulti,
    disabled,
    fieldType,
    Component,
    type = 'text',
    hasLabel = true,
    shouldCustomChange,
    isRequired = false,
    useMenuPortalTarget,
    customChangeHandler,
    customChangeHandler2,
    calendarInputWrapperStyles,
    additionalInputWrapperStyles,
  }, fieldName) => ({
    type,
    isMulti,
    options,
    disabled,
    fieldName,
    Component,
    isRequired,
    shouldCustomChange,
    useMenuPortalTarget,
    customChangeHandler,
    customChangeHandler2,
    calendarInputWrapperStyles,
    [GC.FIELD_TYPE]: R.or(fieldType, type),
    label: G.ifElse(hasLabel, R.of(Array, name)),
    inputWrapperStyles: {
      ...defaultInputWrapperStyles,
      ...G.spreadUiStyles(additionalInputWrapperStyles),
    },
  })),
  R.pick(fields),
)(settings);

const fieldSettings = makeFieldSettings([
  GC.FIELD_PAYROLL_ASSESSORIAL_GUID,
  GC.FIELD_CURRENCY,
  GC.FIELD_AMOUNT,
  GC.FIELD_LIMITED,
  GC.FIELD_TOTAL_AMOUNT,
  GC.FIELD_HISTORICAL_PAID_AMOUNT,
  GC.FIELD_DATE_FROM,
  GC.FIELD_DATE_TO,
  GC.FIELD_DEDUCTION,
  GC.FIELD_ACTIVE,
  GC.FIELD_PROCESS_REMAINING_BALANCE,
  GC.FIELD_PAID_AMOUNT,
  GC.FIELD_FULLY_PAID,
  GC.FIELD_COMMENTS,
]);

const vendorFieldSettings = makeFieldSettings([
  GC.FIELD_PAYROLL_ASSESSORIAL_GUID,
  GC.FIELD_CURRENCY,
  GC.FIELD_AMOUNT,
  GC.FIELD_LIMITED,
  GC.FIELD_TOTAL_AMOUNT,
  GC.FIELD_HISTORICAL_PAID_AMOUNT,
  GC.FIELD_DATE_FROM,
  GC.FIELD_DATE_TO,
  GC.FIELD_PAY_FOR_UNIT,
  GC.FIELD_TRUCK_GUIDS,
  GC.FIELD_DRIVER_GUIDS,
  GC.FIELD_DEDUCTION,
  GC.FIELD_ACTIVE,
  GC.FIELD_PROCESS_REMAINING_BALANCE,
  GC.FIELD_PAID_AMOUNT,
  GC.FIELD_FULLY_PAID,
  GC.FIELD_COMMENTS,
]);

const columnSettings = R.map(R.pick(['type', 'name', 'width', 'filter', 'sortable', 'customComponent']), settings);

const monthlyPaymentColumnSettings = {
  [GC.FIELD_PAYROLL_NUMBER]: {
    width: 300,
    name: 'titles:payroll-number',
    customComponent: ({ data }) => (
      <ActionBox text={data.payrollNumber} action={() => G.goToRoute(routesMap.driverPayrollList)} />
    ),
  },
  [GC.FIELD_PRICE_SHEET_CURRENCY_TOTAL]: {
    width: 200,
    name: 'titles:total',
    customComponent: ({ data = {}, callbackData }) => {
      const { total, currency, priceSheetCurrencyTotal } = data;

      const parentCurrency = G.getPropFromObject(GC.FIELD_CURRENCY, callbackData);

      const displayText = G.ifElse(
        R.equals(priceSheetCurrencyTotal, total),
        `${priceSheetCurrencyTotal} ${parentCurrency}`,
        `${total} ${parentCurrency} (${priceSheetCurrencyTotal} ${currency})`,
      );

      return displayText;
    },
  },
  [GC.FIELD_PAYMENT_MONTH]: {
    width: 150,
    name: 'titles:payment-month',
  },
  [GC.FIELD_PAYMENT_YEAR]: {
    width: 150,
    name: 'titles:payment-year',
  },
};

const monthlyPaymentReport = {
  fields: G.mapIndexed((name, sequence) => ({ name, sequence }), R.keys(monthlyPaymentColumnSettings)),
};

const monthlyPaymentSettings = {
  actionsPicker: [],
  report: monthlyPaymentReport,
  tableCallbackDataProps: [GC.FIELD_CURRENCY],
  columnSettings: monthlyPaymentColumnSettings,
  primaryObjectGuidKey: GC.FIELD_MONTHLY_PAYMENT_GUID,
  endpoints: {
    list: 'driverPayrollChargesByMonthlyPaymentGuid',
  },
};

const vendorMonthlyPaymentSettings = {
  ...monthlyPaymentSettings,
  endpoints: {
    list: 'vendorPayrollChargesByMonthlyPaymentGuid',
  },
};

const MonthlyPayrollDetails = ({ data, settings, parentProps, callbackData }) => {
  const primaryObjectGuid = G.getGuidFromObject(parentProps);
  const index = R.findIndex(R.propEq(primaryObjectGuid, GC.FIELD_GUID), R.pathOr([], ['itemList'], callbackData));
  const groupName = `monthlyPayrollDeductionList.${index}.details`;

  return (
    <RelativeBox zIndex={1}>
      <FormGroupTableWithSimpleCrudOperations
        {...monthlyPaymentSettings}
        {...settings}
        {...R.pick(['openLoader', 'closeLoader', 'getItemListSuccess'], callbackData)}
        margin={20}
        itemList={data}
        isOpened={true}
        groupName={groupName}
        hasFormGroupTitle={false}
        primaryObjectGuid={primaryObjectGuid}
        currency={G.getPropFromObject(GC.FIELD_CURRENCY, parentProps)}
      />
    </RelativeBox>
  );
};

const fieldsWrapperStyles = {
  width: 600,
  flexWrap: 'wrap',
  flexDirection: 'row',
  justifyContent: 'space-between',
};

export const monthlyPayrollDeductionSettings = {
  report,
  defaultValues,
  fieldSettings,
  columnSettings,
  validationSchema,
  fieldsWrapperStyles,
  defaultReportFields,
  withResizableColumns: true,
  withUiFiltersAndEditView: true,
  groupName: 'monthlyPayrollDeductionList',
  includeItemListToTablePropsMemoDeps: true,
  itemTitleArr: ['titles:monthly-payroll-deduction', 'Monthly Payroll Deduction'],
  formGroupTitleArr: ['titles:monthly-payroll-deduction', 'Monthly Payroll Deduction'],
  resetListProps: [
    'resetPayrollLimitedLists',
    'handleToggleResetPayrollLimitedLists',
  ],
  additionalTableSettings: {
    expandableItems: true,
    checkBoxCellWidth: 81,
    searchableTitles: true,
    expandedDetailsComponent: (props) => <MonthlyPayrollDetails {...props} settings={monthlyPaymentSettings} />,
  },
  endpoints: {
    list: 'monthlyPaymentList',
    createOrUpdate: 'monthlyPayment',
    remove: 'getMonthlyPaymentEndpoint',
  },
  tableCallbackDataProps: [
    'itemList',
    'openLoader',
    'closeLoader',
    'accessorialConfigs',
    'getItemListSuccess',
  ],
  // helpers
  makeOptionsForSelect: R.pathOr({}, ['accessorialConfigs']),
  makeInitialValues: (initialValues, { branchConfigs }) => {
    const branchCurrency = G.getConfigValueFromStore(GC.GENERAL_BRANCH_DEFAULT_CURRENCY, branchConfigs);

    return R.assoc(GC.FIELD_CURRENCY, R.pathOr(branchCurrency, [GC.FIELD_CURRENCY], initialValues), initialValues);
  },
};

export const vendorMonthlyPayrollDeductionSettings = {
  ...monthlyPayrollDeductionSettings,
  report: vendorReport,
  defaultReportFields: vendorDefaultReportFields,
  primaryObjectGuidKey: GC.FIELD_FLEET_VENDOR_GUID,
  endpoints: {
    list: 'vendorMonthlyPaymentList',
    createOrUpdate: 'vendorMonthlyPayment',
    remove: 'getVendorMonthlyPaymentEndpoint',
  },
  additionalTableSettings: {
    expandableItems: true,
    checkBoxCellWidth: 81,
    searchableTitles: true,
    expandedDetailsComponent: (props) =>
      <MonthlyPayrollDetails {...props} settings={vendorMonthlyPaymentSettings} />,
  },
  CustomForm: (props) => (
    <VendorMonthlyPayrollDeductionForm
      {...props}
      validationSchema={validationSchema}
      fieldSettings={vendorFieldSettings}
      fieldsWrapperStyles={{
        ...fieldsWrapperStyles,
        overflow: 'hidden auto',
        maxHeight: 'calc(100vh - 155px)',
      }}
    />
  ),
};
